import { useState } from 'react';
import { 
  Clock, DollarSign, User, Phone, MapPin, Heart, 
  Microscope, Stethoscope, Baby, Activity, Syringe, 
  UserCheck, Bone, Eye, Pill, Scissors, Ambulance, 
  FlaskConical, Users, Search, X,
  Dumbbell, Shield, Award, Clock3, MessageCircle, Calendar, CheckCircle
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';

const iconMap: Record<string, React.ElementType> = {
  Stethoscope, Syringe, Heart, Activity, Baby, UserCheck,
  Microscope, Bone, Eye, Pill, Scissors, Ambulance, FlaskConical, Users, Dumbbell
};

const services = [
  // Diagnostic Services
  {
    id: 1,
    icon: 'Microscope',
    category: { en: 'Diagnostic', fr: 'Diagnostic' },
    color: 'from-purple-500 to-purple-600',
    bgLight: 'bg-purple-50',
    iconBg: 'bg-purple-100 text-purple-600',
    title: { en: 'Laboratory', fr: 'Laboratoire' },
    description: { en: 'Medical analysis and diagnostic tests', fr: 'Analyses médicales et tests diagnostiques' },
    schedule: { en: 'Mon-Sat: 7:30 AM - 4:30 PM', fr: 'Lun-Sam: 7h30 - 16h30' },
    specialist: { en: 'Laboratory Technicians', fr: 'Techniciens de Laboratoire' },
    price: { en: 'Affordable rates for all tests', fr: 'Tarifs abordables pour tous les tests' },
    details: { 
      en: 'Our fully equipped laboratory offers a wide range of diagnostic tests including blood analysis, urine tests, microbiology, and more. Results are delivered quickly and accurately.',
      fr: 'Notre laboratoire entièrement équipé offre une large gamme de tests diagnostiques incluant analyses sanguines, tests urinaires, microbiologie, etc. Les résultats sont livrés rapidement et avec précision.'
    },
    features: [
      { en: 'Blood tests', fr: 'Analyses sanguines' },
      { en: 'Urinalysis', fr: 'Analyses d\'urine' },
      { en: 'Microbiology', fr: 'Microbiologie' },
      { en: 'Rapid results', fr: 'Résultats rapides' }
    ],
    emergency: false,
    image: '/About2.png'
  },
  {
    id: 2,
    icon: 'Heart',
    category: { en: 'Diagnostic', fr: 'Diagnostic' },
    color: 'from-red-500 to-red-600',
    bgLight: 'bg-red-50',
    iconBg: 'bg-red-100 text-red-600',
    title: { en: 'Echography', fr: 'Échographie' },
    description: { en: 'Advanced ultrasound imaging', fr: 'Imagerie par échographie avancée' },
    schedule: { en: 'Mon-Sat: 7:30 AM - 4:30 PM', fr: 'Lun-Sam: 7h30 - 16h30' },
    specialist: { en: 'Radiology Specialist', fr: 'Spécialiste en Radiologie' },
    price: { en: 'Competitive pricing', fr: 'Prix compétitifs' },
    details: { 
      en: 'Professional ultrasound services for various medical needs including pregnancy, abdominal, pelvic scans, and more. Our modern equipment ensures clear images and accurate diagnoses.',
      fr: 'Services d\'échographie professionnels pour divers besoins médicaux incluant les examens de grossesse, abdominaux, pelviens, etc. Notre équipement moderne garantit des images claires et des diagnostics précis.'
    },
    features: [
      { en: 'Pregnancy ultrasound', fr: 'Échographie de grossesse' },
      { en: 'Abdominal scans', fr: 'Examens abdominaux' },
      { en: 'Pelvic exams', fr: 'Examens pelviens' },
      { en: 'Doppler imaging', fr: 'Imagerie Doppler' }
    ],
    emergency: false,
    image: '/role.jpeg'
  },
  
  // Surgical Services
  {
    id: 3,
    icon: 'Scissors',
    category: { en: 'Surgery', fr: 'Chirurgie' },
    color: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50',
    iconBg: 'bg-blue-100 text-blue-600',
    title: { en: 'Operating Block', fr: 'Bloc Opératoire' },
    description: { en: '24/7 surgical services', fr: 'Services chirurgicaux 24h/24' },
    schedule: { en: 'Open 24/7, every day', fr: 'Ouvert 24h/24, tous les jours' },
    specialist: { en: 'Surgical Team', fr: 'Équipe Chirurgicale' },
    price: { en: 'Transparent pricing', fr: 'Prix transparents' },
    details: { 
      en: 'Fully equipped operating room available 24 hours a day, 7 days a week for all surgical procedures and emergencies. Our experienced surgical team ensures the highest standards of care.',
      fr: 'Salle d\'opération entièrement équipée disponible 24h/24, 7j/7 pour toutes les interventions chirurgicales et urgences. Notre équipe chirurgicale expérimentée garantit les plus hauts standards de soins.'
    },
    features: [
      { en: 'Emergency surgery', fr: 'Chirurgie d\'urgence' },
      { en: 'Sterile environment', fr: 'Environnement stérile' },
      { en: 'Experienced team', fr: 'Équipe expérimentée' },
      { en: 'Modern equipment', fr: 'Équipement moderne' }
    ],
    emergency: true,
    image: '/pharmacie.jpeg'
  },
  {
    id: 4,
    icon: 'Bone',
    category: { en: 'Surgery', fr: 'Chirurgie' },
    color: 'from-indigo-500 to-indigo-600',
    bgLight: 'bg-indigo-50',
    iconBg: 'bg-indigo-100 text-indigo-600',
    title: { en: 'Traumatology', fr: 'Traumatologie' },
    description: { en: 'Treatment of injuries and fractures', fr: 'Traitement des blessures et fractures' },
    schedule: { en: 'Emergency service 24/7', fr: 'Service d\'urgence 24h/24' },
    specialist: { en: 'Traumatology Specialist', fr: 'Spécialiste en Traumatologie' },
    price: { en: 'Competitive rates', fr: 'Tarifs compétitifs' },
    details: { 
      en: 'Specialized care for accidents, fractures, and musculoskeletal injuries. Our trauma team is available 24/7 for emergencies, providing rapid assessment and treatment.',
      fr: 'Soins spécialisés pour les accidents, fractures et blessures musculo-squelettiques. Notre équipe de traumatologie est disponible 24h/24 pour les urgences, fournissant une évaluation et un traitement rapides.'
    },
    features: [
      { en: 'Fracture treatment', fr: 'Traitement des fractures' },
      { en: 'Sports injuries', fr: 'Blessures sportives' },
      { en: 'Accident care', fr: 'Soins d\'accident' },
      { en: 'Emergency response', fr: 'Réponse d\'urgence' }
    ],
    emergency: true,
    image: '/area.jpeg'
  },

  // Women's Health
  {
    id: 5,
    icon: 'Heart',
    category: { en: 'Women\'s Health', fr: 'Santé Féminine' },
    color: 'from-pink-500 to-pink-600',
    bgLight: 'bg-pink-50',
    iconBg: 'bg-pink-100 text-pink-600',
    title: { en: 'Gynecology', fr: 'Gynécologie' },
    description: { en: 'Women\'s health services', fr: 'Services de santé pour femmes' },
    schedule: { en: 'Consultation by appointment', fr: 'Consultation sur rendez-vous' },
    specialist: { en: 'Gynecologist', fr: 'Gynécologue' },
    price: { en: 'Affordable consultation fees', fr: 'Frais de consultation abordables' },
    details: { 
      en: 'Comprehensive gynecological care including routine exams, reproductive health, and specialized treatments. Our gynecologists provide compassionate, confidential care.',
      fr: 'Soins gynécologiques complets incluant examens de routine, santé reproductive et traitements spécialisés. Nos gynécologues fournissent des soins compatissants et confidentiels.'
    },
    features: [
      { en: 'Routine exams', fr: 'Examens de routine' },
      { en: 'Reproductive health', fr: 'Santé reproductive' },
      { en: 'Menopause care', fr: 'Soins de la ménopause' },
      { en: 'Confidential consultations', fr: 'Consultations confidentielles' }
    ],
    emergency: false,
    image: '/user.jpeg'
  },
  {
    id: 6,
    icon: 'Baby',
    category: { en: 'Women\'s Health', fr: 'Santé Féminine' },
    color: 'from-pink-600 to-pink-700',
    bgLight: 'bg-pink-50',
    iconBg: 'bg-pink-100 text-pink-600',
    title: { en: 'Maternity', fr: 'Maternité' },
    description: { en: 'Complete maternal care 24/7', fr: 'Soins maternels complets 24h/24' },
    schedule: { en: 'Open 24/7, every day', fr: 'Ouvert 24h/24, tous les jours' },
    specialist: { en: 'Obstetricians & Midwives', fr: 'Obstétriciens & Sages-femmes' },
    price: { en: 'Affordable maternity packages', fr: 'Forfaits maternité abordables' },
    details: { 
      en: 'Complete maternity services including prenatal care, delivery, and postnatal support. Our team is available 24/7 to ensure safe and comfortable childbirth experiences.',
      fr: 'Services de maternité complets incluant soins prénatals, accouchement et soutien postnatal. Notre équipe est disponible 24h/24 pour garantir des expériences d\'accouchement sûres et confortables.'
    },
    features: [
      { en: 'Prenatal care', fr: 'Soins prénatals' },
      { en: 'Delivery services', fr: 'Services d\'accouchement' },
      { en: 'Postnatal support', fr: 'Soutien postnatal' },
      { en: '24/7 availability', fr: 'Disponibilité 24h/24' }
    ],
    emergency: true,
    image: '/About2.png'
  },

  // Children's Health
  {
    id: 7,
    icon: 'Baby',
    category: { en: 'Pediatrics', fr: 'Pédiatrie' },
    color: 'from-green-500 to-green-600',
    bgLight: 'bg-green-50',
    iconBg: 'bg-green-100 text-green-600',
    title: { en: 'Pediatrics', fr: 'Pédiatrie' },
    description: { en: 'Specialized care for children 24/7', fr: 'Soins spécialisés pour enfants 24h/24' },
    schedule: { en: 'Open 24/7, every day', fr: 'Ouvert 24h/24, tous les jours' },
    specialist: { en: 'Pediatricians', fr: 'Pédiatres' },
    price: { en: 'Child-friendly rates', fr: 'Tarifs adaptés aux enfants' },
    details: { 
      en: 'Dedicated pediatric care for children of all ages, available around the clock for emergencies and regular consultations. Our child-friendly environment puts young patients at ease.',
      fr: 'Soins pédiatriques dédiés pour les enfants de tous âges, disponibles 24h/24 pour les urgences et les consultations régulières. Notre environnement adapté aux enfants met les jeunes patients à l\'aise.'
    },
    features: [
      { en: 'Newborn care', fr: 'Soins aux nouveau-nés' },
      { en: 'Child development', fr: 'Développement de l\'enfant' },
      { en: 'Vaccinations', fr: 'Vaccinations' },
      { en: 'Emergency care', fr: 'Soins d\'urgence' }
    ],
    emergency: true,
    image: '/doc.jpeg'
  },
  {
    id: 8,
    icon: 'Syringe',
    category: { en: 'Pediatrics', fr: 'Pédiatrie' },
    color: 'from-green-600 to-green-700',
    bgLight: 'bg-green-50',
    iconBg: 'bg-green-100 text-green-600',
    title: { en: 'Vaccination', fr: 'Vaccination' },
    description: { en: 'Immunization services', fr: 'Services de vaccination' },
    schedule: { en: 'Monday to Saturday', fr: 'Lundi à Samedi' },
    specialist: { en: 'Vaccination Nurse', fr: 'Infirmier Vaccinateur' },
    price: { en: 'Affordable vaccination', fr: 'Vaccination abordable' },
    details: { 
      en: 'Complete vaccination services for children and adults following the national immunization schedule. We maintain proper vaccine storage and administration protocols.',
      fr: 'Services de vaccination complets pour enfants et adultes suivant le calendrier vaccinal national. Nous maintenons des protocoles appropriés de stockage et d\'administration des vaccins.'
    },
    features: [
      { en: 'Child vaccinations', fr: 'Vaccinations enfants' },
      { en: 'Adult vaccinations', fr: 'Vaccinations adultes' },
      { en: 'Travel vaccines', fr: 'Vaccins voyage' },
      { en: 'Booster shots', fr: 'Rappels' }
    ],
    emergency: false,
    image: '/About2.png'
  },

  // Specialty Services
  {
    id: 9,
    icon: 'UserCheck',
    category: { en: 'Dental', fr: 'Dentaire' },
    color: 'from-indigo-500 to-indigo-600',
    bgLight: 'bg-indigo-50',
    iconBg: 'bg-indigo-100 text-indigo-600',
    title: { en: 'Stomatology', fr: 'Stomatologie' },
    description: { en: 'Dental and oral healthcare', fr: 'Soins dentaires et buccaux' },
    schedule: { en: 'Mon-Sat: 8:30 AM - 4:30 PM', fr: 'Lun-Sam: 8h30 - 16h30' },
    specialist: { en: 'Stomatologist', fr: 'Stomatologue' },
    price: { en: 'Emergency: 5000 CFA', fr: 'Urgence: 5000 FCFA' },
    details: { 
      en: 'Complete dental and oral healthcare services including consultations, extractions, and treatments. Emergency dental care available.',
      fr: 'Services complets de soins dentaires et buccaux incluant consultations, extractions et traitements. Soins dentaires d\'urgence disponibles.'
    },
    features: [
      { en: 'Dental exams', fr: 'Examens dentaires' },
      { en: 'Extractions', fr: 'Extractions' },
      { en: 'Oral hygiene', fr: 'Hygiène buccale' },
      { en: 'Emergency care', fr: 'Soins d\'urgence' }
    ],
    emergency: true,
    image: '/about3.png'
  },
  {
    id: 10,
    icon: 'Eye',
    category: { en: 'Specialty', fr: 'Spécialités' },
    color: 'from-teal-500 to-teal-600',
    bgLight: 'bg-teal-50',
    iconBg: 'bg-teal-100 text-teal-600',
    title: { en: 'Ophthalmology', fr: 'Ophtalmologie' },
    description: { en: 'Eye care and vision services', fr: 'Soins oculaires et services de vision' },
    schedule: { en: 'Mon-Sat', fr: 'Lun-Sam' },
    specialist: { en: 'Ophthalmologist', fr: 'Ophtalmologue' },
    price: { en: 'Standard consultation rates', fr: 'Tarifs de consultation standard' },
    details: { 
      en: 'Comprehensive eye care services including vision tests, eye disease treatment, and emergency care. Our ophthalmologists use modern diagnostic equipment.',
      fr: 'Services complets de soins oculaires incluant tests de vision, traitement des maladies oculaires et soins d\'urgence. Nos ophtalmologues utilisent un équipement de diagnostic moderne.'
    },
    features: [
      { en: 'Vision tests', fr: 'Tests de vision' },
      { en: 'Eye disease treatment', fr: 'Traitement des maladies oculaires' },
      { en: 'Emergency care', fr: 'Soins d\'urgence' },
      { en: 'Pediatric eye care', fr: 'Soins oculaires pédiatriques' }
    ],
    emergency: true,
    image: '/hero.jpeg'
  },

  // General & Support
  {
    id: 11,
    icon: 'Stethoscope',
    category: { en: 'General Medicine', fr: 'Médecine Générale' },
    color: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50',
    iconBg: 'bg-blue-100 text-blue-600',
    title: { en: 'General Medicine', fr: 'Médecine Générale' },
    description: { en: 'Primary healthcare services', fr: 'Services de soins de santé primaires' },
    schedule: { en: 'Emergency service 24/7', fr: 'Service d\'urgence 24h/24' },
    specialist: { en: 'General Practitioners', fr: 'Médecins Généralistes' },
    price: { en: 'Standard consultation fees', fr: 'Frais de consultation standard' },
    details: { 
      en: 'Comprehensive primary healthcare for common illnesses, preventive care, and health education. Our general practitioners provide personalized care for the whole family.',
      fr: 'Soins de santé primaires complets pour les maladies courantes, soins préventifs et éducation sanitaire. Nos médecins généralistes fournissent des soins personnalisés pour toute la famille.'
    },
    features: [
      { en: 'Common illnesses', fr: 'Maladies courantes' },
      { en: 'Preventive care', fr: 'Soins préventifs' },
      { en: 'Health screenings', fr: 'Dépistages' },
      { en: 'Chronic disease management', fr: 'Gestion des maladies chroniques' }
    ],
    emergency: true,
    image: '/logo.png'
  },
  {
    id: 12,
    icon: 'Ambulance',
    category: { en: 'Support', fr: 'Support' },
    color: 'from-orange-500 to-orange-600',
    bgLight: 'bg-orange-50',
    iconBg: 'bg-orange-100 text-orange-600',
    title: { en: 'Hospitalization', fr: 'Hospitalisation' },
    description: { en: 'Inpatient care services', fr: 'Services de soins hospitaliers' },
    schedule: { en: '24/7 admission available', fr: 'Admission disponible 24h/24' },
    specialist: { en: 'Medical Team', fr: 'Équipe Médicale' },
    price: { en: 'Daily rates available', fr: 'Tarifs journaliers disponibles' },
    details: { 
      en: 'Comfortable hospitalization rooms with 24/7 medical supervision and nursing care. We provide a supportive environment for recovery with attentive staff.',
      fr: 'Chambres d\'hospitalisation confortables avec surveillance médicale et soins infirmiers 24h/24. Nous fournissons un environnement favorable à la guérison avec un personnel attentif.'
    },
    features: [
      { en: 'Private rooms', fr: 'Chambres privées' },
      { en: '24/7 nursing care', fr: 'Soins infirmiers 24h/24' },
      { en: 'Medical supervision', fr: 'Surveillance médicale' },
      { en: 'Comfortable environment', fr: 'Environnement confortable' }
    ],
    emergency: true,
    image: '/About2.png'
  },
  {
    id: 13,
    icon: 'Dumbbell',
    category: { en: 'Support', fr: 'Support' },
    color: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50',
    iconBg: 'bg-blue-100 text-blue-600',
    title: { en: 'Kinesitherapy', fr: 'Kinésithérapie' },
    description: { en: 'Rehabilitation and physiotherapy services', fr: 'Services de rééducation et physiothérapie' },
    schedule: { en: 'Mon-Sat: 8:00 AM - 5:00 PM', fr: 'Lun-Sam: 8h00 - 17h00' },
    specialist: { en: 'Kinesitherapists', fr: 'Kinésithérapeutes' },
    price: { en: 'Competitive rates, insurance accepted', fr: 'Tarifs compétitifs, assurance acceptée' },
    details: { 
      en: 'Professional kinesitherapy services for rehabilitation, mobility improvement, and pain management. Our specialized therapists provide personalized treatment plans for optimal recovery.',
      fr: 'Services professionnels de kinésithérapie pour la rééducation, l\'amélioration de la mobilité et la gestion de la douleur. Nos thérapeutes spécialisés fournissent des plans de traitement personnalisés pour une récupération optimale.'
    },
    features: [
      { en: 'Physical rehabilitation', fr: 'Rééducation physique' },
      { en: 'Sports therapy', fr: 'Thérapie sportive' },
      { en: 'Post-operative care', fr: 'Soins post-opératoires' },
      { en: 'Pain management', fr: 'Gestion de la douleur' },
      { en: 'Mobility exercises', fr: 'Exercices de mobilité' },
      { en: 'Manual therapy', fr: 'Thérapie manuelle' }
    ],
    emergency: false,
    image: '/role.jpeg'
  }
];

export default function ServicesPage() {
  const { language, t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: { en: 'All Services', fr: 'Tous les Services' } },
    { id: 'Diagnostic', name: { en: 'Diagnostic', fr: 'Diagnostic' } },
    { id: 'Surgery', name: { en: 'Surgery', fr: 'Chirurgie' } },
    { id: 'Women\'s Health', name: { en: 'Women\'s Health', fr: 'Santé Féminine' } },
    { id: 'Pediatrics', name: { en: 'Pediatrics', fr: 'Pédiatrie' } },
    { id: 'Dental', name: { en: 'Dental', fr: 'Dentaire' } },
    { id: 'Specialty', name: { en: 'Specialties', fr: 'Spécialités' } },
    { id: 'General Medicine', name: { en: 'General Medicine', fr: 'Médecine Générale' } },
    { id: 'Support', name: { en: 'Support Services', fr: 'Services de Support' } }
  ];

  const filteredServices = services.filter(service => {
    const matchesSearch = language === 'en' 
      ? service.title.en.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.description.en.toLowerCase().includes(searchTerm.toLowerCase())
      : service.title.fr.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.description.fr.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || service.category.en === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen pt-28 pb-20 bg-gradient-to-br from-blue-50 via-white to-amber-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section with Image */}
        <div className="relative mb-12 rounded-3xl overflow-hidden h-80">
          <div className="absolute inset-0">
            <img 
              src="/pharmacie.jpeg" 
              alt="Medical Services"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-600/80"></div>
          </div>
          
          <div className="relative h-full flex items-center justify-center px-6 sm:px-10 lg:px-16">
            <div className="text-center">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                {t('Our Medical Services', 'Nos Services Médicaux')}
              </h1>
              <p className="text-base sm:text-lg text-white/90 max-w-3xl mx-auto mb-8">
                {t(
                  'Complete healthcare services at Clinique Mains Douces Plus - Tradex Simbock',
                  'Services de santé complets à la Clinique Mains Douces Plus - Tradex Simbock'
                )}
              </p>
              
              {/* Emergency Contact Banner */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 max-w-3xl mx-auto border border-white/20">
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <span className="text-white font-bold flex items-center gap-2">
                    <Phone size={18} />
                    653 47 46 14 / 690 64 03 69
                  </span>
                  <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                    24H/24 7j/7
                  </span>
                </div>
                <p className="text-amber-200 font-semibold mt-2 text-lg">
                  "{t('A good diagnosis for a prompt healing!', 'Un bon Diagnostic pour une Prompt Guérison!')}"
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder={t('Search services...', 'Rechercher des services...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mt-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {language === 'en' ? category.name.en : category.name.fr}
              </button>
            ))}
          </div>
        </div>

        {/* Services Grid - Cards Layout with Complete Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredServices.map((service) => {
            const Icon = iconMap[service.icon] || Heart;
            return (
              <div
                key={service.id}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100"
              >
                {/* Image Section */}
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={service.image}
                    alt={language === 'en' ? service.title.en : service.title.fr}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {/* Service Category Badge */}
                  <div className="absolute top-4 left-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                      service.emergency ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
                    }`}>
                      {service.emergency 
                        ? t('24/7 Emergency', 'Urgence 24h/24')
                        : service.category[language]
                      }
                    </span>
                  </div>
                  {/* Icon */}
                  <div className="absolute bottom-4 right-4">
                    <div className={`${service.iconBg} w-14 h-14 rounded-xl flex items-center justify-center shadow-lg`}>
                      <Icon size={28} />
                    </div>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-6">
                  {/* Title and Description */}
                  <div className="mb-4">
                    <h2 className="text-2xl font-bold text-blue-900 mb-2">
                      {language === 'en' ? service.title.en : service.title.fr}
                    </h2>
                    <p className="text-gray-600">
                      {language === 'en' ? service.description.en : service.description.fr}
                    </p>
                  </div>

                  {/* Quick Info Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                    <div className="flex items-center gap-2 text-sm bg-blue-50 p-2 rounded-lg">
                      <Clock size={16} className="text-blue-600 flex-shrink-0" />
                      <span className="text-gray-700 text-xs sm:text-sm">
                        {language === 'en' ? service.schedule.en : service.schedule.fr}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm bg-green-50 p-2 rounded-lg">
                      <User size={16} className="text-green-600 flex-shrink-0" />
                      <span className="text-gray-700 text-xs sm:text-sm">
                        {language === 'en' ? service.specialist.en : service.specialist.fr}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm bg-amber-50 p-2 rounded-lg md:col-span-2">
                      <DollarSign size={16} className="text-amber-600 flex-shrink-0" />
                      <span className="text-gray-700 text-xs sm:text-sm font-medium">
                        {language === 'en' ? service.price.en : service.price.fr}
                      </span>
                    </div>
                  </div>

                  {/* Details Section */}
                  <div className="mb-4">
                    <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <MessageCircle size={16} />
                      {t('Description', 'Description')}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {language === 'en' ? service.details.en : service.details.fr}
                    </p>
                  </div>

                  {/* Features Grid */}
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                      <CheckCircle size={16} />
                      {t('Key Features', 'Caractéristiques')}
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {service.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-1 text-xs sm:text-sm bg-gray-50 p-2 rounded-lg">
                          <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                          <span className="text-gray-700">
                            {language === 'en' ? feature.en : feature.fr}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Emergency indicator */}
                  {service.emergency && (
                    <div className="mt-4 h-1 bg-gradient-to-r from-red-500 to-amber-500 rounded-full"></div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* No Results Message */}
        {filteredServices.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              {t('No services found', 'Aucun service trouvé')}
            </p>
          </div>
        )}

        {/* Emergency Banner */}
        <div className="mt-12 bg-gradient-to-r from-red-600 to-red-800 rounded-3xl p-8 text-white relative overflow-hidden">
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundRepeat: 'repeat'
            }}
          />
          
          <div className="relative">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              {t('24/7 Emergency Services', 'Services d\'Urgence 24h/24')}
            </h2>
            <p className="text-lg mb-6 text-red-100">
              {t(
                'Urgences assurées: Accouchement, Médecine Générale, Pédiatrie, Service Néonatale',
                'Emergency services: Childbirth, General Medicine, Pediatrics, Neonatal Care'
              )}
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="tel:675607705"
                className="inline-flex items-center gap-2 bg-white text-red-600 px-6 py-3 rounded-xl font-semibold hover:bg-red-50 transition transform hover:scale-105"
              >
                <Phone size={18} />
                {t('Call: 675 607 705', 'Appel: 675 607 705')}
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition transform hover:scale-105"
              >
                {t('Contact Us', 'Contactez-Nous')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}